import 'trusted-types';

/**
 * Content-Security-Policy construction, kept free of Angular DI so that it can run
 * before `bootstrapModule()`. A `<meta>` policy only governs content fetched after the
 * element is inserted, so it has to be installed as early as the tenant configuration
 * allows - see `installCsp()`.
 */

export type CspConfig = {
  cspScriptSrc?: string;
  cspConnectSrc?: string;
  cspFrameSrc?: string;
};

/**
 * Joins CSP source expressions, dropping the ones a tenant did not configure. The
 * per-tenant sources arrive from `/ui-api/ui/settings` and are all optional, so a
 * tenant that configures none of them still gets a directive list without empty slots.
 */
function sources(...values: (string | undefined)[]): string {
  return values.filter((value): value is string => !!value && value.trim().length > 0).join(' ');
}

export const TRUSTED_SCRIPT_URLS: readonly string[] = [
  'https://api.usersnap.com',
  'https://help.bankera.com/chatwidget-api/widget/v1/8766d10a-b741-4126-b004-e14142583961',
  'https://help.bankera.com/widget/8e547a17-1a2e-434d-a66e-5f9979f742e4',
  'https://storage.googleapis.com',
  'https://cdn.jsdelivr.net/npm',
  'https://cdn.jsdelivr.net/npm/pdfjs-dist@4.8.69/legacy/build/pdf.worker.min.mjs',
  'https://www.google.com/recaptcha',
  'https://www.gstatic.com/recaptcha/releases',
  'https://cdn.bolddesk.com'
];

export const TRUSTED_FONT_URLS: readonly string[] = ['https://fonts.gstatic.com', 'https://storage.googleapis.com'];

export const TRUSTED_IMAGE_URLS: readonly string[] = [
  'https://images4.bamboohr.com',
  'https://cdn.livechat-files.com',
  'https://storage.googleapis.com',
  'https://help.bankera.com',
  'https://daw.bolddesk.com',
  'https://teroxx.bolddesk.com',
  'https://cdn.bolddesk.com'
];

export const TRUSTED_MEDIA_URLS: readonly string[] = ['https://storage.googleapis.com'];

/**
 * Hosts the BoldDesk chat widget frames its popup from. The widget is loaded as a script from
 * these hosts and then opens the conversation in an iframe of its own, so `frame-src` has to
 * allow them regardless of the per-tenant `cspFrameSrc`, which covers unrelated integrations.
 */
export const TRUSTED_FRAME_URLS: readonly string[] = ['https://help.bankera.com', 'https://daw.bolddesk.com', 'https://teroxx.bolddesk.com'];

/**
 * The tenant's `script-src` additions, as host expressions. `cspScriptSrc` is a
 * space-separated CSP source list supplied per tenant by `/ui-api/ui/settings`;
 * keyword sources (`'self'`, `'unsafe-inline'`, ...) and schemes are not URL prefixes
 * and must not end up in the Trusted Types allowlist.
 */
function tenantScriptUrls(config: CspConfig): string[] {
  return (config.cspScriptSrc ?? '').split(/\s+/).filter((source) => source.startsWith('https://'));
}

/**
 * Script URLs accepted by the Trusted Types `default` policy. This has to track the
 * CSP `script-src` list: a host the tenant is allowed to load scripts from still hits
 * `createScriptURL`, and a throw there breaks the feature just as a CSP block would.
 */
export function trustedScriptUrlsFor(config: CspConfig): string[] {
  return [...TRUSTED_SCRIPT_URLS, ...tenantScriptUrls(config)];
}

/**
 * Whether a script URL is covered by the allowlist.
 *
 * A script element's `src` need not be absolute. `UsersnapService` uses a scheme-relative URL
 * (`//api.usersnap.com/load/<key>.js`), which denotes the same resource as the `https://` form
 * and which CSP accepts, since its host matching resolves the scheme from the document. A raw
 * prefix comparison against an `https://` allowlist does not, so the scheme-relative form is
 * normalised before comparing. Every entry in the allowlist is `https://`, so the scheme is
 * pinned here rather than read from the document - that keeps the decision independent of how
 * the page itself was loaded.
 */
export function isAllowedScriptUrl(input: string, allowedScriptUrls: readonly string[]): boolean {
  const candidates = input.startsWith('//') ? [input, `https:${input}`] : [input];
  return allowedScriptUrls.some((url) => candidates.some((candidate) => candidate.startsWith(url)));
}

export function generateCspPolicy(config: CspConfig): string {
  const scriptSources = sources("'self'", config.cspScriptSrc, ...TRUSTED_SCRIPT_URLS);
  const styleSources = sources("'self'", "'unsafe-inline'", 'https://fonts.googleapis.com', 'https://storage.googleapis.com', 'https://cdn.bolddesk.com');
  const fontSources = sources("'self'", 'data:', ...TRUSTED_FONT_URLS);
  const imageSources = sources("'self'", 'data:', ...TRUSTED_IMAGE_URLS);
  const mediaSources = sources("'self'", ...TRUSTED_MEDIA_URLS);
  const connectSources = sources("'self'", 'blob:', 'https://o1071378.ingest.us.sentry.io', config.cspConnectSrc);
  const frameSources = sources("'self'", config.cspFrameSrc, ...TRUSTED_FRAME_URLS);
  const workerSources = sources("'self'", 'blob:', 'https://cdn.jsdelivr.net');

  let policy = `
      default-src 'self';
      img-src ${imageSources};
      media-src ${mediaSources};
      style-src ${styleSources};
      font-src ${fontSources};
      script-src ${scriptSources};
      script-src-elem ${scriptSources};
      connect-src ${connectSources};
      frame-src ${frameSources};
      child-src 'self';
      worker-src ${workerSources};
    `;

  if (window.trustedTypes) {
    policy += `
        trusted-types angular angular#bundler angular#unsafe-bypass default goog#html;
        require-trusted-types-for 'script';
      `;
  }

  return policy.trim().replace(/\s{2,}/g, ' ');
}

export function createTrustedTypesPolicy(config: CspConfig): void {
  if (!window.trustedTypes) {
    return;
  }

  const allowedScriptUrls = trustedScriptUrlsFor(config);

  try {
    window.trustedTypes.createPolicy('default', {
      createHTML: (input) => input,
      createScript: (input) => input,
      createScriptURL: (input) => {
        if (isAllowedScriptUrl(input, allowedScriptUrls)) {
          return input;
        }
        throw new Error(`ScriptURL not allowed by Trusted Types policy: ${input}`);
      }
    });
  } catch (error) {
    // A 'default' policy can already exist if the app bootstraps more than once in the same
    // window (e.g. Safari restoring a page from the back/forward cache) - Trusted Types throws
    // rather than letting a policy name be redefined.
    console.error('Failed to create Trusted Types policy:', error);
  }
}

export function appendCspMetaTag(policy: string): void {
  const meta = document.createElement('meta');
  meta.httpEquiv = 'Content-Security-Policy';
  meta.content = policy;
  document.head.appendChild(meta);
}

/**
 * Installs the Trusted Types `default` policy and the CSP together. Must be called
 * before the application bootstraps: everything fetched or executed earlier is outside
 * the policy, and CSP is never applied retroactively.
 */
export function installCsp(config: CspConfig): void {
  createTrustedTypesPolicy(config);
  appendCspMetaTag(generateCspPolicy(config));
}
